import { type Event as DeviceEvent } from '@eppendorf/vnls-notifications-utils';
import { DotsLoader } from '@eppendorf/vnls-react-components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { EventRow } from '$features/monitoring/events/components/event-row';
import styles from '$features/monitoring/events/monitoring-events.module.scss';
import { useGetRunRecordEvents } from '$features/report-hub/run-record/run-record-detail.api';
import {
  EventSeverityFilter,
  SeverityFilter,
} from '$features/report-hub/stability-report/severity-filter';

export interface RunRecordEventsProps {
  reportId: string;
  initialSeverity?: EventSeverityFilter;
}

const getSeverityTags = (severity: EventSeverityFilter): string[] => {
  switch (severity) {
    case EventSeverityFilter.Info:
      return ['info', 'notice'];
    case EventSeverityFilter.Warning:
      return ['warning', 'warn'];
    case EventSeverityFilter.Error:
      return ['error'];
    case EventSeverityFilter.Alert:
      return ['alert'];
    default:
      return [];
  }
};

const eventMatchesSeverity = (
  event: Pick<DeviceEvent, 'tags'>,
  severity: EventSeverityFilter,
) => {
  if (severity === EventSeverityFilter.All) {
    return true;
  }

  const tags = (event.tags ?? []).map((tag) => tag.toLowerCase());
  const severityTags = getSeverityTags(severity);

  return severityTags.some((severityTag) => tags.includes(severityTag));
};

/**
 * Run-record events sidecar.
 *
 * Reads `reports/{reportId}/events.json` (uploaded by the device CSV pipeline)
 * and renders the same severity filter + `EventRow` as the stability sidecar.
 *
 * Two failure modes worth distinguishing:
 *  - 404: events.json not produced yet (CSV upload still pending). We show a
 *    "not ready" placeholder, not a generic error.
 *  - other errors: surfaced via `LoadingErrorHint`.
 */
export function RunRecordEvents({ reportId, initialSeverity }: RunRecordEventsProps) {
  const { t } = useTranslation();
  const {
    data: events = [],
    isLoading,
    isError,
    error,
  } = useGetRunRecordEvents(reportId);
  const [selectedSeverity, setSelectedSeverity] = useState<EventSeverityFilter>(
    initialSeverity ?? EventSeverityFilter.All,
  );

  useEffect(() => {
    if (initialSeverity) {
      setSelectedSeverity(initialSeverity);
    }
  }, [initialSeverity]);

  const filteredEvents = useMemo(
    () => events.filter((event) => eventMatchesSeverity(event, selectedSeverity)),
    [events, selectedSeverity],
  );

  if (isLoading) {
    return <DotsLoader className="m-top-10xl m-bottom-10xl" />;
  }

  const isNotReadyYet = isError && error?.response?.status === 404;

  if (isNotReadyYet) {
    return (
      <div className="flex flex__ai--center flex__jc--center color-gray-700 p-xxl">
        {t('runRecordDetail.events.notReadyYet')}
      </div>
    );
  }

  return (
    <>
      <SeverityFilter
        selectedSeverity={selectedSeverity}
        onSeverityChange={setSelectedSeverity}
      />
      <div className={styles.deviceEventHeader}>
        <div>{t('monitoringDetail.eventsDetail.type')}</div>
        <div>{t('monitoringDetail.eventsDetail.time')}</div>
        <div>{t('monitoringDetail.eventsDetail.event')}</div>
      </div>
      <div className="device-event__list">
        {filteredEvents.length === 0 && !isError ? (
          <div className="flex flex__ai--center flex__jc--center font-bold font-size-l p-xxl">
            {t('stabilityDetail.noEventsWithinTimeRange')}
          </div>
        ) : (
          filteredEvents.map((event) => (
            <EventRow key={event.sourceEventId} deviceEvent={event} />
          ))
        )}
      </div>
      {isError && !isNotReadyYet && <LoadingErrorHint className="box p-s m-top-m" />}
    </>
  );
}
